import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, timer } from 'rxjs';

import { select, Store } from '@ngrx/store';
import { MemberDocumentService } from '@ptg-member/services/member-document.service';
import { ActuaryReportAction } from '@ptg-member/store/actions';
import { getActuaryReportState, selectActuaryReportState } from '@ptg-member/store/reducers';
import * as fromReducer from '@ptg-reducers/index';
import { BaseComponent } from '@ptg-shared/components';
import { BannerType } from '@ptg-shared/controls/banner/types/banner.model';
import { Breadcrumb } from '@ptg-shared/types/models/breadcrumb.model';
import { AbstractControlStatus } from '@ptg-shared/types/models/common.model';
import { downloadFile, showBanner } from '@ptg-shared/utils/common.util';
import { filter, map, startWith, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { DOCUMENT_TEMPLATE } from 'src/app/admin/features/template/constants';
import { Sort } from '@angular/material/sort';
import { DEFAULT_PAGE_SIZE, SortType } from '@ptg-shared/constance';
import { FIRST_PAGE, PageEvent } from '@ptg-shared/controls/pagination';
import { Align, Column, Row } from '@ptg-shared/controls/grid';
import { capitalizeFirstLetter, getDateString } from '@ptg-shared/utils/string.util';
import { DocumentListState } from '@ptg-member/store/reducers/member-document.reducer';
import { clearActuayReportStateAction } from '@ptg-member/store/actions/actuary-report.action';
import { DocumentsState } from 'src/app/admin/features/file/store/reducers';
import { getDocumentDownloadAction } from 'src/app/admin/features/file/store/actions/documents.action';

const datePipe = new DatePipe('en-US');

@Component({
  selector: 'ptg-actuary-report',
  templateUrl: './actuary-report.component.html',
  styleUrls: ['./actuary-report.component.scss'],
})
export class ActuaryReportComponent extends BaseComponent implements OnInit {
  readonly BannerType = BannerType;

  listBreadcrumbs: Breadcrumb[] = [
    {
      name: DOCUMENT_TEMPLATE.ACTUARY_REPORT,
    },
  ];

  bannerType: BannerType = BannerType.Hidden;
  message = '';

  editForm!: FormGroup;
  formSubmit$ = new Subject<boolean>();
  downloadCompleted$ = new Subject<void>();
  isGenerateLoading: boolean = false;

  currentYear = new Date().getFullYear();

  tableData: any[] = [];
  isTableLoading: boolean = true;
  sortInfo!: Sort;
  defaultPageSize: number = DEFAULT_PAGE_SIZE;
  pageSize: number = 50;
  pageIndex: number = FIRST_PAGE;
  columns: Column[] = [
    {
      name: 'generatedTime',
      header: {
        title: 'Generated at',
      },
      width: '30px',
      sortable: true,
      truncate: true,
    },
    {
      name: 'fileName',
      header: {
        title: 'File Name',
      },
      width: '150px',
      truncate: true,
    },
    {
      name: 'periodDate',
      header: {
        title: 'Period',
      },
      width: '100px',
      truncate: true,
    },
    
    {
      name: 'generatedBy',
      header: {
        title: 'Generated by',
      },
      width: '100px',
      truncate: true,
    },
    {
      name: 'status',
      header: {
        title: 'Status',
      },
      width: '100px',
      truncate: true,
    },
  ];
  totalRecords: number | any;
  downloadFileName: string = '';

  constructor(
    private fb: FormBuilder,
    private store: Store<fromReducer.State>,
    private memberDocumentService: MemberDocumentService,
    private datePipe: DatePipe,
    private memberDocumentStore: Store<DocumentListState>,
    private documentStore: Store<DocumentsState>,

  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initFormGroup();
    this.submitForm();
    this.getData();
    this.selectGeneratedReportsInformationSelector();
    
  }

  submitForm() {
    this.formSubmit$
      .pipe(
        tap(() => {
          this.editForm.markAllAsTouched();
        }),
        switchMap(() =>
          this.editForm.statusChanges.pipe(
            startWith(this.editForm.status),
            filter((status) => status !== AbstractControlStatus.PENDING),
            take(1),
          ),
        ),
        filter((status) => status === AbstractControlStatus.VALID),
      )
      .subscribe(() => {
        this.onClickExport();
      });
  }

  initFormGroup() {
    this.editForm = this.fb.group({
      startDate: this.fb.control(new Date(`07/01/${this.currentYear - 1}`), { validators: [Validators.required] }),
      endDate: this.fb.control(new Date(`06/30/${this.currentYear}`), { validators: [Validators.required] })
    });
  }

  controlDate() {
    let startDate = this.editForm?.get('startDate')?.value;
    let endDate = this.editForm?.get('endDate')?.value;

    if (startDate && endDate) {
      if (endDate < startDate || endDate === startDate) {
        this.editForm.get('startDate')?.setErrors({ customStartDateError: 'Period End Date must be after Period Start Date.' });
        this.editForm.get('endDate')?.setErrors({ customEndDateError: 'Period End Date must be after Period Start Date.' });
      } else {
        this.editForm.get('startDate')?.setErrors(null);
        this.editForm.get('endDate')?.setErrors(null);
      }
    }
  }

  private _getExportActuaryReportState() {
      this.store.pipe(
        select(selectActuaryReportState),
        takeUntil(this.unsubscribe$),
        filter((state) => !!state)
      ).subscribe(state => {
        if (state?.isLoading) {
          const message = 'Generating actuary report might take a while. You can perform other actions on this screen once completed. You may continue working in Pension Pro+ while the process is running.';
          showBanner.call(this, BannerType.Info, '', '', { customMessage: message });
        } else {
          if (state?.success) {
            this.bannerType = BannerType.Hidden;
          } else if (!state?.success) {
            showBanner.call(this, BannerType.Fail, '', '', { customMessage: 'Error occurred while generating actuary report, please try again.' });
          }
  
          this.store.dispatch(ActuaryReportAction.clearSetExportActuaryReportStateAction());
        }
      });
    }

  selectGeneratedReportsInformationSelector() {
    this.memberDocumentStore
      .select(getActuaryReportState)
      .pipe(
        filter((response) => !!response && !response.isLoading),
        tap((response) => {
          if (this.isTableLoading) {
            this.isTableLoading = !!response?.isLoading;
          }
        }),
        map((response) => response?.payload),
        tap((data) => {
          this.totalRecords = data?.total as number;
          this.tableData =
            data?.actuaryReport?.map((item: any) => {
              return {
                ...item,
                generatedTime: this.datePipe.transform(getDateString(item.generatedTime), 'MM/dd/yyyy h:mm a'),
                periodDate:  (item.periodStartDate ? this.datePipe.transform(getDateString(item.periodStartDate), 'MM/dd/yyyy', 'UTC') : '') + ' - ' +(item.periodEndDate ? this.datePipe.transform(getDateString(item.periodEndDate), 'MM/dd/yyyy', 'UTC') : ''),
              };
            }) ?? [];
            
            // Check if fileName column exists and has string values
            const isInProgress = this.tableData.length > 0 && 
              this.tableData.some(item => item.hasOwnProperty('status') && 
                                        typeof item.status === 'string' && 
                                        item.status.trim() === 'InProgress');

            if (!isInProgress) {
              this.isGenerateLoading = false;
            }else {
              this.isGenerateLoading = true;
            }

          this.memberDocumentStore.dispatch(clearActuayReportStateAction());
        }),
        switchMap(() => timer(3000)),
        takeUntil(this.unsubscribe$),
      ).subscribe(() => {
      this.getData();
    });
  }

  downloadLetter(row: Row) {
    if (!row) {
      return;
    }
    this.downloadFileName = row?.fileName;
    this.documentStore.dispatch(
      getDocumentDownloadAction({ fileId: row?.attachmentId as string, fileName: row.fileName as string }),
    );
  }

  onClickExport() {
    this.isGenerateLoading = true;
    this.store.dispatch(ActuaryReportAction.setExportActuaryReportStateAction());

    const startDate = this.editForm.get('startDate')?.value;
    const endDate = this.editForm.get('endDate')?.value;
    
    const message = 'Generating actuary report might take a while. You can perform other actions on this screen once completed. You may continue working in Pension Pro+ while the process is running.';
    showBanner.call(this, BannerType.Info, '', '', { customMessage: message });

    this.memberDocumentService.generateActuaryReport(
      {
        startDate: datePipe.transform(startDate, 'yyyy-MM-ddTHH:mm:ss') ?? '',
        endDate: datePipe.transform(endDate, 'yyyy-MM-ddTHH:mm:ss') ?? '',
        generatedAt: datePipe.transform(new Date(), 'yyyy-MM-ddTHH:mm:ss') ?? ''
      }
    ).subscribe(
      (res) => {
        if (res?.fileName) {
        } else {
          this.store.dispatch(ActuaryReportAction.setExportActuaryReportStateFailureAction());
        }
      },
      (err) => {
        this.store.dispatch(ActuaryReportAction.setExportActuaryReportStateFailureAction());
      }
    );
  }

  getData() {
    let sortType = SortType.DESC;
    let sortNames = 'GeneratedTime';
    if (this.sortInfo?.active && this.sortInfo?.direction) {
      sortNames = capitalizeFirstLetter(this.sortInfo.active);
      sortType = this.sortInfo.direction === 'desc' ? SortType.DESC : SortType.ASC;
    }

    this.store.dispatch(ActuaryReportAction.getActuayReportAction({
      request: {
        sortNames,
        sortType,
        pageNumber: this.pageIndex,
        pageSize: this.pageSize,
      },
    }));
  }

  onChangePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageNumber;
    this.isTableLoading = true;
    this.getData();
  }

  onSortChange(event: Sort) {
    this.sortInfo = event;
    this.isTableLoading = true;
    this.getData();
  }
}

